import React, { useState } from "react";
import { PageHeader, Content } from "@/components/PageChrome";
import { api, formatApiError } from "@/lib/api";
import {
  ShieldCheck, ShieldOff, Eye, Pencil, Building2, Check, X, Info,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCompanies } from "@/context/CompanyContext";
import { usePermissionRequests } from "@/context/PermissionRequestContext";
import OutgoingRequests from "@/components/OutgoingRequests";
import { Link } from "react-router-dom";

function Toggle({ checked, onChange, label, hint, disabled, testid }) {
  return (
    <label
      className={`flex items-start gap-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}
      data-testid={`${testid}-row`}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`mt-0.5 w-10 h-5 rounded-full shrink-0 transition-colors relative ${
          checked ? "bg-[var(--primary)]" : "bg-gray-300"
        } disabled:cursor-not-allowed`}
        data-testid={testid}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
            checked ? "left-5" : "left-0.5"
          }`}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

function ChangePasswordPlaceholder() {
  return (
    <div className="text-sm text-gray-500" data-testid="settings-security">
      Password changes are currently handled by an administrator. Contact your
      administrator if you need your password reset.
    </div>
  );
}

export default function Settings() {
  const { user, refresh: refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const { companies, activeCompany, limit, used, canAddCompany, refresh: refreshCompanies } =
    useCompanies();
  const { pending, requests, respond } = usePermissionRequests();
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState("access");

  const canView = !!user?.allow_admin_data_view;
  const canModify = !!user?.allow_admin_data_modify;

  // Consent lives on the user record and is only writable by its owner, so every
  // toggle is a plain PUT to /auth/consent followed by a /auth/me re-sync.
  const setConsent = async (patch) => {
    const key = Object.keys(patch)[0];
    setBusy(key);
    try {
      await api.put("/auth/consent", patch);
      await refreshUser();
      if (patch.allow_admin_data_view === false) {
        toast.success("Administrator access revoked");
      } else if (patch.allow_admin_data_view === true) {
        toast.success("Administrators can now view your data");
      } else if (patch.allow_admin_data_modify === false) {
        toast.success("Administrator edit access revoked");
      } else if (patch.allow_admin_data_modify === true) {
        toast.success("Administrators can now view and edit your data");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const past = requests.filter((r) => r.status !== "pending");

  const TABS = [
    { id: "access", label: "Administrator access", icon: ShieldCheck },
    { id: "requests", label: `Requests${pending.length ? ` (${pending.length})` : ""}`, icon: Info },
    { id: "companies", label: "Companies", icon: Building2 },
    { id: "security", label: "Password", icon: ShieldOff },
  ];

  return (
    <>
      <PageHeader
        overline="Account"
        title="Settings"
        description="Control what administrators can do with your data."
        testid="page-settings"
      />

      <Content>
        <div className="flex gap-1 mb-5 border-b border-[var(--border)] overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const on = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 h-10 text-sm inline-flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${
                  on
                    ? "border-[var(--primary)] text-[var(--primary)] font-medium"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`settings-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "access" && (
          <div className="space-y-5 max-w-2xl" data-testid="settings-access">
            <div className="card-flat p-5">
              <div className="flex items-start gap-3 mb-4">
                {canModify ? (
                  <ShieldCheck className="w-5 h-5 text-[var(--primary)] shrink-0 mt-0.5" />
                ) : canView ? (
                  <Eye className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                ) : (
                  <ShieldOff className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                )}
                <div>
                  <div className="font-medium">Administrator access</div>
                  <p className="text-xs text-gray-500 mt-1">
                    These switches control administrator access to your business data.
                    You can grant or withdraw them at any time; an administrator can
                    also update them directly from the admin panel.
                  </p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[var(--border)]">
                <Toggle
                  testid="toggle-view"
                  label="Allow administrators to view my data"
                  hint="Read-only access to your clients, documents and company profile."
                  checked={canView}
                  disabled={busy !== null}
                  onChange={(v) => setConsent({ allow_admin_data_view: v })}
                />
                <Toggle
                  testid="toggle-modify"
                  label="Allow administrators to edit my data"
                  hint="Full management: create and edit clients, documents, company profiles and your account details. Turning this off keeps read-only view access."
                  checked={canModify}
                  disabled={busy !== null || !canView}
                  onChange={(v) => setConsent({ allow_admin_data_modify: v })}
                />
              </div>

              {canView && (
                <button
                  onClick={() =>
                    setConsent({
                      allow_admin_data_view: false,
                      allow_admin_data_modify: false,
                    })
                  }
                  disabled={busy !== null}
                  className="mt-5 text-sm px-4 h-9 border border-[var(--border)] rounded-md hover:bg-gray-50 text-red-700 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="revoke-all-access"
                >
                  <ShieldOff className="w-4 h-4" /> Revoke all administrator access
                </button>
              )}
            </div>

            <div className="card-flat p-5">
              <div className="overline">Your account</div>
              <dl className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">Name</dt>
                  <dd className="mt-0.5">{user?.name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Email</dt>
                  <dd className="mt-0.5">{user?.email || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Role</dt>
                  <dd className="mt-0.5 capitalize">{user?.role || "user"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Active company</dt>
                  <dd className="mt-0.5">{activeCompany?.name || "—"}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div className="space-y-4 max-w-2xl" data-testid="settings-requests">
            {pending.length === 0 && past.length === 0 && (
              <div className="card-flat p-8 text-center">
                <Info className="w-7 h-7 text-gray-300 mx-auto" />
                <div className="text-sm font-medium mt-2.5">No requests yet</div>
                <p className="text-xs text-gray-500 mt-1">
                  {isAdmin
                    ? "No one has asked you for access. Requests you send to other users are listed below."
                    : "If an administrator needs access they will ask here first. You decide whether to approve it."}
                </p>
              </div>
            )}

            {/* An admin's inbox above only ever contains requests addressed *to*
                them, which is empty unless another admin asked them for something.
                Showing the outgoing side too is what makes the section meaningful
                for an administrator. */}
            {isAdmin && (
              <div className="pt-2" data-testid="settings-outgoing">
                <OutgoingRequests />
              </div>
            )}

            {pending.map((r) => (
              <div key={r.id} className="card-flat p-4 border-l-2 border-l-[var(--primary)]"
                   data-testid={`settings-request-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm">
                      <strong>{r.requested_by_email || "An administrator"}</strong> requests
                      permission to <strong>{r.scope === "modify" ? "view and edit" : "view"}</strong> your data.
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      Requested {r.created_at ? new Date(r.created_at).toLocaleString() : "recently"}
                    </div>
                    {r.reason && (
                      <div className="text-xs text-gray-600 mt-2 p-2 rounded bg-gray-50 border border-[var(--border)]">
                        “{r.reason}”
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => respond(r.id, "approve")}
                          disabled={busy === r.id}
                          className="btn-primary text-xs h-8 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`settings-approve-${r.id}`}>
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => respond(r.id, "reject")}
                          disabled={busy === r.id}
                          className="text-xs h-8 px-3 border border-[var(--border)] rounded-md hover:bg-gray-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`settings-reject-${r.id}`}>
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
            ))}

            {past.length > 0 && (
              <div>
                <div className="overline mb-2">History</div>
                <div className="card-flat divide-y divide-[var(--border)]" data-testid="settings-request-history">
                  {past.map((r) => (
                    <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 text-xs">
                      <div className="min-w-0">
                        <div className="truncate">
                          {r.requested_by_email || "Administrator"} asked for{" "}
                          {r.scope === "modify" ? "view and edit" : "view"} access
                        </div>
                        {r.responded_at && (
                          <div className="text-gray-400 mt-0.5">
                            {new Date(r.responded_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-[11px] ${
                          r.status === "approved"
                            ? "bg-green-50 text-green-700"
                            : r.status === "rejected"
                              ? "bg-red-50 text-red-700"
                              : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "companies" && (
          <div className="space-y-4 max-w-2xl" data-testid="settings-companies">
            <div className="card-flat p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Company profiles</div>
                  <p className="text-xs text-gray-500 mt-1">
                    You are using {used} of {limit}. Switch the active company any time from
                    the header.
                  </p>
                </div>
                <Link to="/app/companies"
                   className="text-sm px-4 h-9 border border-[var(--border)] rounded-md leading-9 hover:bg-gray-50 inline-flex items-center gap-2">
                  <Pencil className="w-3.5 h-3.5" /> Manage
                </Link>
              </div>
              <div className="mt-4 space-y-2">
                {companies.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm px-3 h-10 border border-[var(--border)] rounded-md">
                    <span className="truncate">{c.name || "Unnamed company"}</span>
                    <span className="text-[11px] text-gray-500 shrink-0 ml-2">
                      {c.doc_count || 0} docs · {c.client_count || 0} clients
                    </span>
                  </div>
                ))}
              </div>
              {!canAddCompany && (
                <div className="text-[11px] text-gray-500 mt-3">
                  Company limit reached. An administrator can raise it for your account.
                </div>
              )}
            </div>
            <button onClick={refreshCompanies}
                    className="text-xs px-3 h-8 border border-[var(--border)] rounded-md hover:bg-gray-50">
              Refresh
            </button>
          </div>
        )}

        {tab === "security" && (
          <div className="card-flat p-5 max-w-2xl">
            <div className="overline mb-3">Password</div>
            <ChangePasswordPlaceholder />
          </div>
        )}
      </Content>
    </>
  );
}